import { Save as SaveIcon } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { AppConfigSelect } from "../../../../components/app-config-select";
import { TagChip } from "../../../../components/tag-chip";
import { SUGGESTED_TAGS } from "../../../../constants/tags";
import {
  useAddTagsToTest,
  useAvailableTags,
  useRemoveTagsFromTest,
} from "../../../../hooks/use-tags";
import type { Test, UserFlowPriority, UserFlowSeverity } from "../../../../types";
import { getTagStyle } from "../../../../utils/tag-styles";

interface AdvancedTabProps {
  test: Test | null | undefined;
  onUpdate: (data: {
    priority?: UserFlowPriority;
    severity?: UserFlowSeverity;
    is_positive?: boolean;
    reasoning?: string;
    success_criteria?: string;
    app_config_id?: string;
    _url: string;
  }) => void;
  isUpdating: boolean;
}

const PRIORITY_OPTIONS: UserFlowPriority[] = ["low", "medium", "high", "urgent"];
const SEVERITY_OPTIONS: UserFlowSeverity[] = ["low", "medium", "high", "critical"];

export function AdvancedTab({ test, onUpdate, isUpdating }: AdvancedTabProps) {
  const [priority, setPriority] = useState<UserFlowPriority | "">(test?.priority || "");
  const [severity, setSeverity] = useState<UserFlowSeverity | "">(test?.severity || "");
  const [isPositive, setIsPositive] = useState<string>(
    test?.is_positive === false ? "false" : "true",
  );
  const [reasoning, setReasoning] = useState(test?.reasoning || "");
  const [successCriteria, setSuccessCriteria] = useState(test?.success_criteria || "");
  const [tags, setTags] = useState<string[]>(test?.tags || []);
  const [appConfigId, setAppConfigId] = useState<string>(test?.app_config_id || "");

  const { data: availableTagsData } = useAvailableTags();
  const addTagsMutation = useAddTagsToTest();
  const removeTagsMutation = useRemoveTagsFromTest();

  // Update local state when test data changes
  useEffect(() => {
    if (test) {
      setPriority(test.priority || "");
      setSeverity(test.severity || "");
      setIsPositive(test.is_positive === false ? "false" : "true");
      setReasoning(test.reasoning || "");
      setSuccessCriteria(test.success_criteria || "");
      setTags(test.tags || []);
      setAppConfigId(test.app_config_id || "");
    }
  }, [test]);

  const handleTagsChange = (newTags: string[]) => {
    if (!test) return;

    // Find tags to add
    const tagsToAdd = newTags.filter((tag) => !tags.includes(tag));
    // Find tags to remove
    const tagsToRemove = tags.filter((tag) => !newTags.includes(tag));

    // Update local state immediately
    setTags(newTags);

    // Handle additions
    if (tagsToAdd.length > 0) {
      addTagsMutation.mutate({
        tags: tagsToAdd,
        _url: `/tests/${test.id}/tags`,
      } as { tags: string[]; _url: string });
    }

    // Handle removals
    if (tagsToRemove.length > 0) {
      removeTagsMutation.mutate({
        tags: tagsToRemove,
        _url: `/tests/${test.id}/tags`,
      } as { tags: string[]; _url: string });
    }
  };

  const handleSave = () => {
    if (!test) return;

    onUpdate({
      priority: priority || undefined,
      severity: severity || undefined,
      is_positive: isPositive === "true",
      reasoning: reasoning || undefined,
      success_criteria: successCriteria || undefined,
      app_config_id: appConfigId || "",
      _url: `/tests/${test.id}`,
    });
  };

  const hasChanges = () => {
    if (!test) return false;
    return (
      (priority || "") !== (test.priority || "") ||
      (severity || "") !== (test.severity || "") ||
      isPositive !== (test.is_positive === false ? "false" : "true") ||
      reasoning !== (test.reasoning || "") ||
      successCriteria !== (test.success_criteria || "") ||
      appConfigId !== (test.app_config_id || "")
    );
  };

  if (!test) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">No test selected</Typography>
      </Box>
    );
  }

  const availableTags = availableTagsData?.tags || [];
  const allSuggestedTags = [...new Set([...availableTags, ...SUGGESTED_TAGS])];

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        {/* Classification and Test Type Row */}
        <Grid container spacing={2}>
          {/* Classification Section */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2, height: "100%" }}>
              <Typography variant="h6" gutterBottom>
                Classification
              </Typography>
              <Stack spacing={2} sx={{ mt: 1 }}>
                {/* Priority and Severity in same row */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <FormLabel sx={{ mb: 0.5 }}>Priority</FormLabel>
                      <Select
                        value={priority}
                        onChange={(e) => setPriority(e.target.value as UserFlowPriority)}
                        displayEmpty
                        size="small"
                      >
                        <MenuItem value="">
                          <em>Not set</em>
                        </MenuItem>
                        {PRIORITY_OPTIONS.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option.charAt(0).toUpperCase() + option.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <FormLabel sx={{ mb: 0.5 }}>Severity</FormLabel>
                      <Select
                        value={severity}
                        onChange={(e) => setSeverity(e.target.value as UserFlowSeverity)}
                        displayEmpty
                        size="small"
                      >
                        <MenuItem value="">
                          <em>Not set</em>
                        </MenuItem>
                        {SEVERITY_OPTIONS.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option.charAt(0).toUpperCase() + option.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Tags */}
                <Box>
                  <FormLabel sx={{ mb: 0.5, display: "block" }}>Tags</FormLabel>
                  <Autocomplete
                    multiple
                    size="small"
                    options={allSuggestedTags}
                    value={tags}
                    onChange={(_, value) => handleTagsChange(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Type to search or create tags..."
                        size="small"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <TagChip
                          {...getTagProps({ index })}
                          key={option}
                          tag={option}
                          size="small"
                        />
                      ))
                    }
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      const style = getTagStyle(option);
                      return (
                        <Box
                          key={key}
                          component="li"
                          {...otherProps}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: style.color,
                            },
                          }}
                        >
                          <TagChip tag={option} size="small" variant="outlined" sx={{ mr: 1 }} />
                        </Box>
                      );
                    }}
                    freeSolo
                    autoComplete
                    includeInputInList
                    filterSelectedOptions
                    clearOnBlur
                    handleHomeEndKeys
                  />
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* Test Type Section */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2, height: "100%" }}>
              <Typography variant="h6" gutterBottom>
                Test Type
              </Typography>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ mb: 0.5 }}>
                    Path Type
                  </FormLabel>
                  <RadioGroup
                    value={isPositive}
                    onChange={(e) => setIsPositive(e.target.value)}
                    row
                  >
                    <FormControlLabel
                      value="true"
                      control={<Radio size="small" />}
                      label="Happy Path"
                    />
                    <FormControlLabel
                      value="false"
                      control={<Radio size="small" />}
                      label="Unhappy Path"
                    />
                  </RadioGroup>
                </FormControl>

                {/* Success/Failure Criteria */}
                <FormControl fullWidth>
                  <FormLabel sx={{ mb: 0.5 }}>
                    {isPositive === "false" ? "Failure Criteria" : "Success Criteria"}
                  </FormLabel>
                  <TextField
                    multiline
                    rows={3}
                    value={successCriteria}
                    onChange={(e) => setSuccessCriteria(e.target.value)}
                    placeholder={
                      isPositive === "false"
                        ? "Define what constitutes failure for this test..."
                        : "Define what constitutes success for this test..."
                    }
                    size="small"
                    fullWidth
                  />
                </FormControl>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {/* App Config Section */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2, height: "100%" }}>
              <Typography variant="h6" gutterBottom>
                App Config
              </Typography>
              <Box sx={{ mt: 1 }}>
                <AppConfigSelect
                  value={appConfigId}
                  onChange={(newValue) => setAppConfigId(newValue || "")}
                  size="small"
                  label=""
                  placeholder="Select an app config..."
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Reasoning Section */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Reasoning
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <TextField
              multiline
              rows={4}
              value={reasoning}
              onChange={(e) => setReasoning(e.target.value)}
              placeholder="Document the reasoning behind this test's design and implementation..."
              size="small"
              fullWidth
            />
          </FormControl>
        </Paper>

        {/* Save Button */}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges() || isUpdating}
          >
            {isUpdating ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
